import { useQueryClient } from '@tanstack/react-query';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChevronRight, Close, FormatListBulleted } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ChatIcon from '@mui/icons-material/Chat';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { ActivityMetadataModel, EntityMetadataModel } from '../../../api/api';
import { BoxResizeHandle } from '../../../common/components/BoxResizeHandle';
import useAsyncMemo from '../../../common/hooks/useAsyncMemo';
import useAsyncValue from '../../../common/hooks/useAsyncValue';
import ActionPanelTabContent from '../../../components/ActionPanelTabContent';
import PromptChat from '../../../components/Chat/PromptChat';
import { ChatMessage } from '../../../components/Chat/PromptChatAdapter';
import { isChangeChatMessage } from '../../../components/Chat/isChangeChatMessage';
import useFlowMetadata from '../Hooks/Metadata/useFlowMetadata';
import usePointerSettledValue from '../Hooks/usePointerSettledValue';
import useFlowGraphState from '../Hooks/ReactFlow/useFlowGraphState';
import { useIsAgentToolActivity } from '../Hooks/useAgentToolGraph';
import { useFlowDesignerChatAdapter } from '../Hooks/useFlowDesignerChatAdapter';
import useFlowQueries from '../Hooks/useFlowQueries';
import { FlowDefinitionModel, FlowEdgeModel, FlowNodeModel, TransitionModel } from '../Models/FlowModel';
import { ActivityNode, FlowEdge, FlowNode, TransitionEdge } from '../Models/GraphModel';
import ActivityVariableScopeCalculatorService from '../Services/ActivityVariableScopeCalculatorService';
import { getAgentToolTransitionsForActivity, isAgentToolTransition } from '../Services/AgentToolGraph';
import { IEntitySchemaRepository } from '../Services/EntitySchemaRepository';
import { createFlowVariableScope } from '../Services/FlowVariableScopeFactory';
import { ActivityProperties } from './ActivityProperties';
import ActivityGallery from './ActivityGallery';
import { FlowProperties } from './FlowProperties';
import { resolveFlowSchemaValue } from '../Services/FlowSchemaValues';
import { TransitionProperties } from './TransitionProperties';
import { updateAgentToolInputOverride } from '../Services/AgentToolInputOverrides';

const LOCAL_STORAGE_KEY = 'actionPanel.width';
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 200;
const MAX_WIDTH = 800;

export const SECTION_PROPERTIES = 'properties' as const;
export const SECTION_ADD_ACTIVITY = 'addActivity' as const;
export const SECTION_CHAT = 'chat' as const;

export type SectionType = typeof SECTION_PROPERTIES | typeof SECTION_ADD_ACTIVITY | typeof SECTION_CHAT;

function collectSchemaRefIdsByContext(value: unknown, idsByContext = new Map<string, Set<string>>(), visited = new Set<object>()): Map<string, Set<string>> {
	if (!value || typeof value !== 'object') {
		return idsByContext;
	}

	if (visited.has(value)) {
		return idsByContext;
	}
	visited.add(value);

	const record = value as Record<string, unknown>;
	const contextId = record.ContextId ?? record.contextId;
	const id = record.Id ?? record.id;
	if (typeof contextId === 'string' && typeof id === 'string') {
		if (!idsByContext.has(contextId)) {
			idsByContext.set(contextId, new Set<string>());
		}
		idsByContext.get(contextId)!.add(id);
	}

	for (const childValue of Object.values(record)) {
		if (Array.isArray(childValue)) {
			childValue.forEach((item) => collectSchemaRefIdsByContext(item, idsByContext, visited));
		} else {
			collectSchemaRefIdsByContext(childValue, idsByContext, visited);
		}
	}

	return idsByContext;
}

type ActionPanelProps = {
	flowDefinitionId?: string;
	addActivity: (activity: ActivityMetadataModel) => void;
	flow: FlowDefinitionModel;
	onNodeDataChanged?: (id: string, model: FlowNodeModel) => void;
	onEdgeDataChanged?: (id: string, model: FlowEdgeModel) => void;
	onFlowChanged: (flow: FlowDefinitionModel) => void;
	entitySchemaRepository: IEntitySchemaRepository;
	readOnly?: boolean;
	drawer?: boolean;
	onClose?: () => void;
};

type InspectorGraph = ReturnType<typeof useFlowGraphState<FlowNode, FlowEdge>>;

export function ActionPanel(props: ActionPanelProps) {
	const graph = useFlowGraphState<FlowNode, FlowEdge>();
	const settledGraph = usePointerSettledValue(graph);
	const pending = graph.selectedNode?.id !== settledGraph.selectedNode?.id || graph.selectedEdge?.id !== settledGraph.selectedEdge?.id;
	const container = useRef<HTMLDivElement>(null);
	useEffect(() => {
		container.current?.toggleAttribute('inert', pending);
	}, [pending]);
	return <Box ref={container} aria-busy={pending} sx={{ display: 'flex', minHeight: 0, flexShrink: 0, width: props.drawer ? '100%' : undefined, height: props.drawer ? '100%' : undefined, pointerEvents: pending ? 'none' : undefined }}>
		<ActionPanelContent {...props} graph={settledGraph} />
	</Box>;
}

const ActionPanelContent = memo(function ActionPanelContent({
	graph,
	flowDefinitionId,
	addActivity,
	onNodeDataChanged,
	onEdgeDataChanged,
	flow,
	onFlowChanged,
	entitySchemaRepository,
	readOnly,
	drawer = false,
	onClose,
}: ActionPanelProps & { graph: InspectorGraph }) {
	// External state for resize functionality
	const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_WIDTH);
	const panelRef = useRef<HTMLDivElement>(null);

	const [expandedSection, setExpandedSection] = useState<SectionType>(SECTION_PROPERTIES);

	const flowQueries = useFlowQueries();
	const queryClient = useQueryClient();

	const { selectedNode, nodes, selectedEdge, edges } = graph;
	const selectedNodeId = selectedNode?.id;
	const selectedEdgeId = selectedEdge?.id;
	const selectedActivityIsAgentTool = useIsAgentToolActivity(edges, selectedNode?.id, nodes);
	const selectedActivityToolTransitions = useMemo(() => getAgentToolTransitionsForActivity(edges, selectedNode?.id, nodes), [edges, selectedNode?.id, nodes]);
	const selectedActivityInputOverrides = useMemo(() => {
		if (!selectedNode || selectedActivityToolTransitions.length !== 1) return undefined;

		return selectedActivityToolTransitions
			.flatMap((toolTransition) => (toolTransition.data as TransitionModel | undefined)?.InputOverrides ?? [])
			.filter((inputOverride) => inputOverride.ActivityId === selectedNode.id)
			.reduce<Record<string, { enabled?: boolean | undefined; description?: string; bound?: boolean }>>((result, inputOverride) => {
				result[inputOverride.PropertyName] = {
					enabled: inputOverride.Enabled ?? undefined,
					description: inputOverride.Description,
					bound: inputOverride.Override !== undefined,
				};
				return result;
			}, {});
	}, [selectedActivityToolTransitions, selectedNode]);
	const selectedActivityInputOverrideDescriptions = useMemo(() => {
		if (!selectedActivityInputOverrides) {
			return undefined;
		}

		return Object.entries(selectedActivityInputOverrides).reduce<Record<string, string>>((result, [propertyName, override]) => {
			if (override?.description) {
				result[propertyName] = override.description;
			}
			return result;
		}, {});
	}, [selectedActivityInputOverrides]);
	const selectedActivityInputOverrideEnabled = useMemo(() => {
		if (!selectedActivityInputOverrides) {
			return undefined;
		}

		return Object.entries(selectedActivityInputOverrides).reduce<Record<string, boolean | undefined>>((result, [propertyName, override]) => {
			result[propertyName] = override?.enabled;
			return result;
		}, {});
	}, [selectedActivityInputOverrides]);
	const selectedActivityBoundInputs = useMemo(() => Object.keys(selectedActivityInputOverrides ?? {})
		.filter((name) => selectedActivityInputOverrides?.[name].bound), [selectedActivityInputOverrides]);
	const handleAgentToolInputOverrideChanged = useCallback(
		(propertyName: string, enabled: boolean | undefined) => {
			if (!selectedNode || selectedActivityToolTransitions.length !== 1 || readOnly) return;

			for (const toolTransition of selectedActivityToolTransitions) {
				const transitionData = (toolTransition.data as TransitionModel | undefined) ?? {
					Id: toolTransition.id,
					To: selectedNode.id,
				};
				const existingOverrides = transitionData.InputOverrides ?? [];
				const nextOverrides = updateAgentToolInputOverride(existingOverrides, selectedNode.id, propertyName, { Enabled: enabled });

				onEdgeDataChanged?.(toolTransition.id, {
					...transitionData,
					InputOverrides: nextOverrides,
				});
			}
		},
		[selectedActivityToolTransitions, onEdgeDataChanged, readOnly, selectedNode],
	);
	const handleAgentToolInputDescriptionOverrideChanged = useCallback(
		(propertyName: string, description: string | undefined) => {
			if (!selectedNode || selectedActivityToolTransitions.length !== 1 || readOnly) return;

			for (const toolTransition of selectedActivityToolTransitions) {
				const transitionData = (toolTransition.data as TransitionModel | undefined) ?? {
					Id: toolTransition.id,
					To: selectedNode.id,
				};
				const nextOverrides = updateAgentToolInputOverride(transitionData.InputOverrides, selectedNode.id, propertyName, { Description: description?.trim() || undefined });

				onEdgeDataChanged?.(toolTransition.id, {
					...transitionData,
					InputOverrides: nextOverrides,
				});
			}
		},
		[selectedActivityToolTransitions, onEdgeDataChanged, readOnly, selectedNode],
	);
	const resetAgentToolInputOverrides = useCallback(() => {
		if (!selectedNode || selectedActivityToolTransitions.length !== 1 || readOnly) return;

		for (const toolTransition of selectedActivityToolTransitions) {
			const transitionData = toolTransition.data as TransitionModel | undefined;
			const remainingOverrides = (transitionData?.InputOverrides ?? []).filter((inputOverride) => inputOverride.ActivityId !== selectedNode.id);
			onEdgeDataChanged?.(toolTransition.id, {
				...(transitionData ?? { Id: toolTransition.id, To: selectedNode.id }),
				InputOverrides: remainingOverrides.length > 0 ? remainingOverrides : undefined,
			});
		}
	}, [selectedActivityToolTransitions, onEdgeDataChanged, readOnly, selectedNode]);
	const chatContext = useMemo(
		() => ({
			currentActivityId: selectedNode?.id ?? selectedEdge?.source,
			currentTransitionId: selectedEdge?.id,
			nodes,
		}),
		[selectedNode?.id, selectedEdge?.source, selectedEdge?.id, nodes],
	);
	const chatAdapter = useFlowDesignerChatAdapter(flowDefinitionId, chatContext);
	const handleAssistantMessageReceived = useCallback(
		async (message: ChatMessage) => {
			if (!isChangeChatMessage(message) || !flowDefinitionId) {
				return;
			}

			await queryClient.invalidateQueries({ queryKey: ['flow', 'getById', flowDefinitionId], exact: true });
		},
		[flowDefinitionId, queryClient],
	);

	const { flowMetadata } = useFlowMetadata();

	useEffect(() => {
		if (selectedNodeId || selectedEdgeId) {
			// When node or edge is selected, show properties
			setExpandedSection(SECTION_PROPERTIES);
		} else {
			// When nothing selected, show add activity
			setExpandedSection(SECTION_ADD_ACTIVITY);
		}
	}, [selectedNodeId, selectedEdgeId]);

	const [scopeMetadataRevision, setScopeMetadataRevision] = useState(0);
	useEffect(() => queryClient.getQueryCache().subscribe((event) => {
		const [family, kind] = event.query.queryKey;
		const relevant = (family === 'flow' && ['entities', 'entity', 'operation', 'integrationEvent', 'byId'].includes(String(kind)))
			|| (family === 'service-connection' && kind === 'property');
		if (relevant && (event.type === 'removed' || (event.type === 'updated' && ['success', 'invalidate'].includes(event.action.type)))) {
			setScopeMetadataRevision((revision) => revision + 1);
		}
	}), [queryClient]);

	const scopeInputSchema = resolveFlowSchemaValue(flow, flow?.InputSchemaValue);
	const scopeOutputSchema = resolveFlowSchemaValue(flow, flow?.OutputSchemaValue);
	const contextEntityMetadataModel = useAsyncMemo(
		() => createFlowVariableScope(flow?.InputSchema, flow?.OutputSchema,
			scopeInputSchema, scopeOutputSchema, entitySchemaRepository),
		[flow?.InputSchema, flow?.OutputSchema, scopeInputSchema, scopeOutputSchema, entitySchemaRepository, scopeMetadataRevision],
	);

	useEffect(() => {
		const idsByContext = collectSchemaRefIdsByContext(flow);
		idsByContext.forEach((ids, contextId) => {
			const entityIds = Array.from(ids);
			if (entityIds.length === 0) {
				return;
			}

			queryClient.fetchQuery(flowQueries.queryEntitySchemasByIds(contextId, entityIds)).then((entities) => {
				queryClient.setQueryData<EntityMetadataModel[]>(flowQueries.entitySchemasOfContextQueryKey(contextId), (cachedEntities) => {
					const entitiesById = new Map((cachedEntities ?? []).map((entity) => [entity.id, entity]));
					entities.forEach((entity) => entitiesById.set(entity.id, entity));
					return Array.from(entitiesById.values());
				});
			});
		});
	}, [flow, flowQueries, queryClient]);

	const scopeCache = useMemo(() => ({ calculator: undefined as ActivityVariableScopeCalculatorService | undefined }),
		[flowDefinitionId, contextEntityMetadataModel, flowMetadata, flowQueries, queryClient, scopeMetadataRevision]);
	const variableScopeResult = useAsyncValue(async () => {
		const activityVariableScopeCalculatorService = contextEntityMetadataModel
			? scopeCache.calculator?.withSchemaValues(flow?.SchemaValues ?? []).withGraph(nodes, edges)
				?? new ActivityVariableScopeCalculatorService(flowMetadata, contextEntityMetadataModel, nodes, edges, flowQueries, queryClient, flow?.SchemaValues)
			: undefined;
		scopeCache.calculator = activityVariableScopeCalculatorService;
		if (activityVariableScopeCalculatorService) {
			if (selectedEdge) {
				// Select the activity at the start of the edge
				const transitionVariableScope = await activityVariableScopeCalculatorService.getVariableScopeAtActivity(selectedEdge.source);
				return transitionVariableScope.leave;
			}
			if (selectedNode) {
				const activityVariableScope = await activityVariableScopeCalculatorService.getVariableScopeAtActivity(selectedNode.id);
				return activityVariableScope.enter;
			}
		}

		if (!selectedNode && !selectedEdge) {
			return contextEntityMetadataModel;
		}

		return undefined;
	}, [scopeCache, contextEntityMetadataModel, flowMetadata, nodes, edges, selectedNode, selectedEdge, flowQueries, queryClient], { clearOnChange: false });
	const variableScope = variableScopeResult.value;
	// Tool bindings execute while the caller runs, before its response/output assignments exist.
	const toolInputScopeResult = useAsyncValue(async () => {
		if (!selectedEdge || !isAgentToolTransition(selectedEdge) || !scopeCache.calculator) return undefined;
		return (await scopeCache.calculator.getVariableScopeAtActivity(selectedEdge.source)).enter;
	}, [selectedEdge, scopeCache, variableScopeResult.value]);

	const handleTabChange = useCallback((_: React.SyntheticEvent, value: SectionType) => setExpandedSection(value), []);

	useEffect(() => {
		if (readOnly && expandedSection !== SECTION_PROPERTIES) {
			setExpandedSection(SECTION_PROPERTIES);
		}
	}, [expandedSection, readOnly]);

	return (
		<Paper
			ref={panelRef}
			sx={{
				position: 'relative',
				width: drawer ? '100%' : `${panelWidth}px`,
				height: drawer ? '100%' : undefined,
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
				flexShrink: 0,
				overflow: 'hidden',
			}}
		>
			{/* Drag Handle */}
			{!drawer && (
				<BoxResizeHandle
					position="left"
					width={panelWidth}
					setWidth={setPanelWidth}
					panelRef={panelRef}
					storageKey={LOCAL_STORAGE_KEY}
					defaultWidth={DEFAULT_WIDTH}
					minWidth={MIN_WIDTH}
					maxWidth={MAX_WIDTH}
					direction="right"
				/>
			)}

			{/* Content */}
			<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
					<Tabs value={expandedSection} onChange={handleTabChange} variant="fullWidth" sx={{ flexGrow: 1, minWidth: 0 }}>
						<Tab
							value={SECTION_PROPERTIES}
							icon={<FormatListBulleted fontSize="small" />}
							iconPosition="start"
							label="Properties"
							sx={{ minHeight: 48, fontSize: '0.75rem' }}
						/>
						<Tab
							value={SECTION_ADD_ACTIVITY}
							icon={<AddCircleIcon fontSize="small" />}
							iconPosition="start"
							label="Add Activity"
							disabled={readOnly}
							sx={{ minHeight: 48, fontSize: '0.75rem' }}
						/>
						<Tab
							value={SECTION_CHAT}
							icon={<ChatIcon fontSize="small" />}
							iconPosition="start"
							label="Chat"
							disabled={readOnly}
							sx={{ minHeight: 48, fontSize: '0.75rem' }}
						/>
					</Tabs>
					{onClose && (
						<Tooltip title={drawer ? 'Close tools panel' : 'Collapse tools panel'}>
							<IconButton aria-label={drawer ? 'Close tools panel' : 'Collapse tools panel'} onClick={onClose} sx={{ mr: 0.5 }}>
								{drawer ? <Close /> : <ChevronRight />}
							</IconButton>
						</Tooltip>
					)}
				</Box>

				<ActionPanelTabContent active={expandedSection === SECTION_PROPERTIES} innerBoxSx={{ overflowY: 'auto' }}>
					{variableScopeResult.isLoading && variableScope === undefined && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, color: 'text.secondary' }}>
							<CircularProgress size={16} thickness={5} />
							<Typography variant="caption">Calculating variable scope...</Typography>
						</Box>
					)}
					<Stack direction="column" spacing={2}>
						{selectedNode && selectedActivityToolTransitions.length > 1 && <Alert severity="info">This tool has multiple callers. Select an AgentTool transition to edit that caller’s input settings. Activity-level input edits are disabled to avoid changing every caller.</Alert>}
						{selectedNode && (
							<ActivityProperties
								key={selectedNode.id}
								activity={selectedNode as ActivityNode}
								onActivityDataChanged={readOnly ? undefined : onNodeDataChanged}
								variableScope={variableScope}
								isVariableScopeLoading={variableScopeResult.isLoading}
								readOnly={readOnly}
								isAgentTool={selectedActivityIsAgentTool}
								agentToolInputOverrides={selectedActivityInputOverrideEnabled}
								agentToolBoundInputs={selectedActivityBoundInputs}
								agentToolInputDescriptions={selectedActivityInputOverrideDescriptions}
								onAgentToolInputOverrideChanged={readOnly || selectedActivityToolTransitions.length !== 1 ? undefined : handleAgentToolInputOverrideChanged}
								onAgentToolInputDescriptionChanged={readOnly || selectedActivityToolTransitions.length !== 1 ? undefined : handleAgentToolInputDescriptionOverrideChanged}
								onResetAgentToolInputOverrides={readOnly || selectedActivityToolTransitions.length !== 1 ? undefined : resetAgentToolInputOverrides}
							/>
						)}
						{selectedEdge && (
							<TransitionProperties
								key={selectedEdge.id}
								transition={selectedEdge as TransitionEdge}
								toolActivity={nodes.find((node) => node.id === selectedEdge.target) as ActivityNode | undefined}
								toolInputVariableScope={toolInputScopeResult.value}
								isToolInputScopeLoading={toolInputScopeResult.isLoading}
								flow={flow}
								onTransitionDataChanged={readOnly ? undefined : onEdgeDataChanged}
								variableScope={variableScope}
								isVariableScopeLoading={variableScopeResult.isLoading}
								readOnly={readOnly}
							/>
						)}
						{!selectedNode && !selectedEdge && (
							<FlowProperties
								flow={flow}
								onFlowChanged={onFlowChanged}
								variableScope={variableScope}
								isVariableScopeLoading={variableScopeResult.isLoading}
								readOnly={readOnly}
							/>
						)}
					</Stack>
				</ActionPanelTabContent>

				<ActionPanelTabContent active={expandedSection === SECTION_ADD_ACTIVITY} innerBoxSx={{ display: 'flex', flexDirection: 'column' }}>
					<ActivityGallery activities={flowMetadata.activities} onSelect={addActivity} />
				</ActionPanelTabContent>

				<ActionPanelTabContent active={expandedSection === SECTION_CHAT} innerBoxSx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
					<PromptChat
						adapter={chatAdapter}
						defaultTitle="Randol Flow Assistant"
						showConversationNameBar
						emptyStateText="Ask me anything about this flow."
						unavailableText="Save the flow to start chatting."
						onAssistantMessageReceived={handleAssistantMessageReceived}
					/>
				</ActionPanelTabContent>
			</Stack>
		</Paper>
	);
});
